<script setup lang="ts">
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import { CheckCircleIcon } from '@heroicons/vue/20/solid';

import useMailboxes from '@/modules/settings/composables/useMailboxes';
import useTeams from '@/modules/settings/composables/useTeams';
import useUsers from '@/modules/settings/composables/useUsers';
import type { IRoles } from '@/modules/settings/interfaces/Users';

import ButtonsModal from '@/modules/shared/components/ButtonsModal.vue';
import InputMultiple from '@/modules/shared/components/InputMultiple.vue';
import SelectMultiple from '@/modules/shared/components/SelectMultiple';
import selectSimple from '@/modules/shared/components/SelectSimple';
import useModal from '@/modules/shared/composable/useModal';
import useUserInfo from '@/modules/shared/composable/useUserInfo';

import MODALS from '@/modules/shared/utils/modals_enum';
import { emailReg } from '@/modules/shared/utils/reg';
import ToastDemoComponent from '@/modules/shared/components/ToastDemoComponent.vue';

const toast = useToast();
const { userInfo } = useUserInfo();

const { onlyExistEmails, createUsers, getUsers } = useUsers();
const { editUserData, hideModal, activeModal } = useModal();
const email = ref('');
const emails = ref<{ item: string; status: true; notTraking: boolean }[]>([]);
const { mailboxes } = useMailboxes();
const { teams } = useTeams();
const hasDataModal = activeModal.value === MODALS.updateUser;

if (hasDataModal) {
    emails.value = [
        {
            item: editUserData.value.email,
            status: true,
            notTraking: !(mailboxes.value.filter((p: { email: string }) => p.email === editUserData.value.email).length > 0)
        }
    ];
}

const userRoleTitle: IRoles[] = [
    {
        title: 'Admin',
        description: 'Can see email statistics for all tracked mailboxes.'
    },
    {
        title: 'Manager',
        description: 'Can see email statistics only for the mailboxes you give permission to.'
    },
    {
        title: 'Viewer',
        description: 'Can only see their own email statistics.'
    }
];

const findRoles = (role: string): IRoles => userRoleTitle[userRoleTitle.findIndex((i) => i.title === role)];

const role = ref(
    hasDataModal
        ? findRoles(editUserData.value.role)
        : {
              title: '',
              description: ''
          }
);

const mailboxesValues = ref(hasDataModal && editUserData.value?.mailboxes ? editUserData.value?.mailboxes?.map((m: string) => m) : []);

const mailboxesOptions = ref(mailboxes.value.map((m: { email: string }) => m.email));

const teamsValues = ref(hasDataModal && editUserData.value?.teams ? editUserData.value?.teams?.map((m: string) => m) : []);
const teamsOptions = ref(teams.value.map((m: { name: string }) => m.name));

const isDisabled = () => {
    if ((role.value as IRoles)?.title === 'Manager' && mailboxesValues.value?.length === 0 && teamsValues.value?.length === 0) return true;
    if (email.value.length === 0 && emails.value.length > 0 && !isDisabledText() && role.value?.title) return false;
    return true;
};

const isDisabledText = () => emails.value.filter((e: { status: boolean }) => e.status === false).length > 0;
const onlyItems = () => emails.value.map((e: { item: string }) => e.item);

const ConfirmAddMail = () => {
    if (onlyItems().length + onlyExistEmails.value.length <= userInfo.value.maxUsers) {
        const teamsIds: string[] = [];
        if (teamsValues.value.length) {
            teamsValues.value.forEach((t: string) => {
                teams.value.forEach((team: { id: string; name: string }) => {
                    if (t === team.name) teamsIds.push(team.id);
                });
            });
        }

        const newUserData = {
            id: hasDataModal ? editUserData.value.id : '',
            emails: onlyItems(),
            role: (role.value as IRoles).title.toLowerCase(),
            manage_mailboxes: mailboxesValues.value.length > 0 ? mailboxesValues.value : [],
            manage_teams: teamsValues.value
        };

        if (newUserData.role === 'viewer' && emails.value.some((p: { notTraking: boolean }) => p.notTraking)) {
            hideModal();
            toast.error(`${emails.value.length > 1 ? 'These users doesn’t' : 'This user don’t'} have a tracked ${emails.value.length > 1 ? 'mailboxes' : 'mailbox'}`);
        } else {
            createUsers(newUserData).then(async () => {
                await getUsers();
                hideModal();
                toast.success(ToastDemoComponent, { icon: CheckCircleIcon });
            });
        }
    }
};
</script>

<template>
    <p class="text-gray-900 font-semibold leading-8 text-lg p-6 border-b border-gray-200">{{ hasDataModal ? 'Edit role' : 'Add new users' }}</p>
    <div class="p-6 border-b border-gray-200 overflow-auto">
        <p v-if="!hasDataModal" class="text-sm leading-5 font-normal text-gray-700 pb-8">Enter the email addresses of the users you’d like to add and choose their roles.</p>
        <div class="pb-6">
            <label for="email" class="block text-sm font-medium leading-5 text-gray-700 pb-1">Email address</label>

            <InputMultiple v-model:text="email" v-model:items="emails" :disabled="hasDataModal" :reg="(e: string) => !emailReg.test(e)" :allowedDomains="[]" :onlyExistEmails="[]" />
            <span v-show="isDisabledText()" class="text-red-400 block mt-1 text-left text-xs font-inter font-medium"> Please check that all email addresses are correct. </span>
        </div>
        <div>
            <selectSimple v-model:values="role" :options="userRoleTitle" label="User role" placeholder="Choose user role" />
        </div>
        <div v-if="(role as IRoles)?.title === 'Manager'" class="pt-6 flex flex-col gap-6">
            <div><SelectMultiple v-model:values="teamsValues" :options="teamsOptions" placeholder="Select teams" label="Managed teams" /></div>
            <div><SelectMultiple v-model:values="mailboxesValues" :options="mailboxesOptions" placeholder="Select mailboxes" label="Managed mailboxes" /></div>
        </div>
    </div>
    <ButtonsModal :isDisabled="isDisabled()" @submit="ConfirmAddMail" />
</template>
